import { useCallback, useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';

import {
  listDeviceShares,
  shareDevice,
  unshareDevice,
  type Device,
  type DeviceShare,
} from '../api/devices';
import { extractErrorMessage } from '../api/errors';

/**
 * Which of your tenants a tablet works in (DEVICE-CROSS-TENANT-SHARING).
 *
 * The rows are the tenants YOU administer, not every tenant on the platform —
 * that list is the customer list. So an empty matrix means "this is your only
 * tenant", which the dialog says outright rather than showing a bare table.
 *
 * Toggling writes immediately (one row, one request) instead of collecting a
 * set and saving: sharing hardware is a small, reversible act, and a Save
 * button would leave the operator wondering whether a half-applied batch
 * took. Each row carries its own pending + error state for the same reason.
 */
export function DeviceSharingDialog({
  open,
  tenantId,
  device,
  canWrite,
  onClose,
  onChanged,
}: {
  open: boolean;
  tenantId: string;
  device: Device | null;
  /** Read-only admins see the matrix but can't change it. */
  canWrite: boolean;
  onClose: () => void;
  /** Fired after a successful change, so the Devices list can refresh. */
  onChanged?: () => void;
}) {
  const [rows, setRows] = useState<DeviceShare[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!device) return;
    setError(null);
    try {
      setRows(await listDeviceShares(tenantId, device.id));
    } catch (e) {
      setError(extractErrorMessage(e));
      setRows([]);
    }
  }, [tenantId, device]);

  useEffect(() => {
    if (!open) return;
    setRows(null);
    void load();
  }, [open, load]);

  const toggle = async (row: DeviceShare, next: boolean) => {
    if (!device) return;
    setPending(row.tenant_id);
    setError(null);
    try {
      if (next) await shareDevice(tenantId, device.id, row.tenant_id);
      else await unshareDevice(tenantId, device.id, row.tenant_id);
      await load();
      onChanged?.();
    } catch (e) {
      setError(extractErrorMessage(e));
    } finally {
      setPending(null);
    }
  };

  const others = (rows ?? []).filter((r) => !r.is_home);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Share {device?.label ?? 'device'}
        <Typography variant="body2" color="text.secondary">
          The same tablet can record for any of your tenants. The operator
          picks which one on the capture app before a case.
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {rows === null ? (
          <Stack alignItems="center" sx={{ py: 3 }}>
            <CircularProgress size={24} />
          </Stack>
        ) : rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {device?.hardware_id
              ? 'You administer only this tenant, so there is nowhere to share this device.'
              : 'This device has not reported its device ID yet. Open the capture app on it once, then share it.'}
          </Typography>
        ) : (
          <Stack spacing={0.5}>
            {rows
              .filter((r) => r.is_home)
              .map((r) => (
                <Row
                  key={r.tenant_id}
                  name={r.tenant_name}
                  slug={r.tenant_slug}
                  detail="This tenant — retire the device here to remove it"
                  checked
                  disabled
                />
              ))}
            {others.map((r) => (
              <Row
                key={r.tenant_id}
                name={r.tenant_name}
                slug={r.tenant_slug}
                detail={
                  r.shared && r.label && r.label !== device?.label
                    ? `Named “${r.label}” there`
                    : undefined
                }
                checked={r.shared}
                disabled={!canWrite || pending !== null}
                busy={pending === r.tenant_id}
                onChange={(next) => void toggle(r, next)}
              />
            ))}
            {others.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                You administer only this tenant, so there is nowhere to share
                this device.
              </Typography>
            )}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Row({
  name,
  slug,
  detail,
  checked,
  disabled,
  busy,
  onChange,
}: {
  name: string;
  slug: string;
  detail?: string;
  checked: boolean;
  disabled?: boolean;
  busy?: boolean;
  onChange?: (next: boolean) => void;
}) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ py: 0.5 }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
            {name}
          </Typography>
          <Chip size="small" variant="outlined" label={slug} sx={{ height: 18, fontSize: 11 }} />
        </Stack>
        {detail && (
          <Typography variant="caption" color="text.secondary">
            {detail}
          </Typography>
        )}
      </Box>
      <Stack direction="row" alignItems="center" spacing={1}>
        {busy && <CircularProgress size={16} />}
        <Switch
          size="small"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange?.(e.target.checked)}
          inputProps={{ 'aria-label': `Share with ${name}` }}
        />
      </Stack>
    </Stack>
  );
}
